/**
 * Card component for content containers.
 * 
 * @module components/ui/Card
 */

import { cn } from '../../lib/utils';
import type { CardProps, StatCardProps } from '../../types';

/**
 * Padding styles mapping.
 */
const paddingStyles: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

/**
 * Card component for grouping related content.
 * 
 * @param props - Card properties
 * @returns Card element
 * 
 * @example
 * <Card title="User Info" action={<Button>Edit</Button>}>
 *   <p>Content here</p>
 * </Card>
 */
export function Card({
  title,
  children,
  className,
  action,
  padding = 'md',
}: CardProps) {
  return (
    <div className={cn('card', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          )}
          {action && <div>{action}</div>}
        </div>
      )}
      <div className={paddingStyles[padding]}>{children}</div>
    </div>
  );
}

/**
 * StatCard component for dashboard metrics.
 *
 * @param props - StatCard properties
 * @returns StatCard element
 */
export function StatCard({ title, value, change, icon, subtitle }: StatCardProps) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {change && (
            <p
              className={cn(
                'mt-2 text-sm font-medium flex items-center',
                change.isPositive ? 'text-success-600' : 'text-error-600'
              )}
            >
              <span>{change.isPositive ? '↑' : '↓'}</span>
              <span className="ml-1">{Math.abs(change.value)}%</span>
              <span className="ml-1 text-gray-500">vs last period</span>
            </p>
          )}
          {subtitle && (
            <p className="mt-1 text-xs text-gray-400">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className="p-3 bg-primary-100 rounded-xl text-primary-600">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
